// Rolling a saving throw from the initiative row.
//
// Reported 2026-10-01: the DM wants to roll a save for whoever is in the row in
// front of them, off that token's own ability scores, with a plain d20 when
// there are none and a bonus they can set on the fly.
//
// The honesty rule here is the one that matters. A row backed by a character
// sheet has a real saving throw modifier; a goblin someone dropped on the map
// has nothing at all. Those are different answers and the DM has to be able to
// tell them apart, because "+0" looks like a fact and "no sheet" is a question.
// So the source travels with the bonus and is shown on the roll.
import { rollDice } from "./auras";
import { abilityMod, type AbilityKey, type Character } from "./types";

export const ABILITIES: AbilityKey[] = ["str", "dex", "con", "int", "wis", "cha"];
export const ABILITY_LABEL: Record<AbilityKey, string> = { str: "STR", dex: "DEX", con: "CON", int: "INT", wis: "WIS", cha: "CHA" };

export type SaveBasis = "save" | "ability" | "flat";
export interface SaveBonus { bonus: number; basis: SaveBasis; why: string }

const finite = (v: unknown): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** What this row adds to a d20 for the given save.
 *
 *  A recorded saving throw modifier wins; failing that, the ability modifier
 *  derived from the score (a creature with a sheet but no proficiency still
 *  rolls its DEX); failing that, nothing but the DM's own bonus.
 *
 *  A recorded ZERO is a real answer and is kept. Number("") and Number(null) are
 *  both 0 and both finite, so a blank read as a modifier of +0 would look exactly
 *  like a genuine +0 - the same trap that wrote blanks onto real sheets as zeroes
 *  on 2026-10-01. Absent and none are different, and only one is a number. */
export function saveBonusFor(character: Character | null | undefined, ability: AbilityKey, extra = 0): SaveBonus {
  const add = finite(extra) ?? 0;
  const save = character ? finite(character.savingThrows?.[ability]) : null;
  if (save !== null) return { bonus: save + add, basis: "save", why: `${ABILITY_LABEL[ability]} save ${sign(save)}` };
  const score = character ? finite(character.abilities?.[ability]) : null;
  if (score !== null) return { bonus: abilityMod(score) + add, basis: "ability", why: `${ABILITY_LABEL[ability]} ${score} (${sign(abilityMod(score))})` };
  return { bonus: add, basis: "flat", why: "no sheet — plain d20" };
}

export const sign = (n: number) => (n >= 0 ? `+${n}` : String(n));

export interface SaveRoll { d20: number; bonus: number; total: number; basis: SaveBasis; why: string; dc?: number; passed?: boolean }

/** Roll it. The d20 comes from the same crypto-backed roller the aura dice use,
 *  so a save at the table is not Math.random. */
export function rollSave(b: SaveBonus, dc?: number, d20 = rollDice("1d20")): SaveRoll {
  const total = d20 + b.bonus;
  const target = finite(dc);
  return { d20, bonus: b.bonus, total, basis: b.basis, why: b.why, ...(target === null ? {} : { dc: target, passed: total >= target }) };
}

/** The line the DM reads in the activity rail. */
export function describeSaveRoll(name: string, ability: AbilityKey, r: SaveRoll): string {
  const head = `${name}: ${ABILITY_LABEL[ability]} save ${r.total}`;
  const maths = `d20 ${r.d20} ${sign(r.bonus)}`;
  const verdict = r.dc === undefined ? "" : ` · DC ${r.dc} — ${r.passed ? "passed" : "failed"}`;
  return `${head} (${maths})${verdict}`;
}
